import { Badge } from "@mantine/core";
import { Money, Bps } from "@/components/Money";
import { Mono } from "@/components/Mono";
import { Timestamp } from "@/components/Timestamp";
import { RelatedLink } from "@/components/RelatedLink";
import { humanize } from "@/lib/format";
import { addressUrl } from "@/lib/explorer";
import { ExternalMono } from "@/components/Mono";
import { COLLECTIONS } from "@/firestore/collections";
import type {
  EarnOperationDoc,
  EarnPositionDoc,
  VaultApprovalDoc,
  VaultDoc,
  WithDocId,
} from "@/firestore/types";
import type { EntityConfig } from "../types";
import { monoCol, moneyCol, relatedCol, stateCol, textCol, timeCol } from "../shared";

type P<T> = WithDocId<T>;

export const earnPositionConfig: EntityConfig<EarnPositionDoc> = {
  key: "earnPosition",
  collection: COLLECTIONS.earnPositions,
  title: "Earn Position",
  pluralTitle: "Earn Positions",
  ownerField: "owner",
  defaultOrder: [["updatedAt", "desc"]],
  columns: [
    monoCol<P<EarnPositionDoc>>("id", "ID", (r) => r.id),
    textCol<P<EarnPositionDoc>>("vault", "Vault", (r) => r.vault),
    moneyCol<P<EarnPositionDoc>>("balance", "Token balance", (r) => r.tokenBalanceMicroUsdc),
    relatedCol<P<EarnPositionDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<EarnPositionDoc>>("lastSyncedAt", "Synced", (r) => r.lastSyncedAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Vault", value: <RelatedLink entity="vault" id={d.vault} label={d.vault} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.wallet} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Token balance", value: <Money micro={d.tokenBalanceMicroUsdc} fw={600} /> },
    { label: "Supplied all-time", value: <Money micro={d.suppliedAllTimeMicroUsdc} /> },
    { label: "Withdrawn all-time", value: <Money micro={d.withdrawnAllTimeMicroUsdc} /> },
    { label: "Last synced", value: <Timestamp value={d.lastSyncedAt} absolute /> },
  ],
};

export const earnOperationConfig: EntityConfig<EarnOperationDoc> = {
  key: "earnOperation",
  collection: COLLECTIONS.earnOperations,
  title: "Earn Operation",
  pluralTitle: "Earn Operations",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<EarnOperationDoc>>("id", "ID", (r) => r.id),
    stateCol<P<EarnOperationDoc>>((r) => r.state),
    textCol<P<EarnOperationDoc>>("type", "Type", (r) => r.type, { humanize: true }),
    textCol<P<EarnOperationDoc>>("vault", "Vault", (r) => r.vault),
    moneyCol<P<EarnOperationDoc>>("amount", "Requested", (r) => r.requestedAmountMicroUsdc),
    relatedCol<P<EarnOperationDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<EarnOperationDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Type", value: humanize(d.type) },
    { label: "Vault", value: <RelatedLink entity="vault" id={d.vault} label={d.vault} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.wallet} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Requested", value: <Money micro={d.requestedAmountMicroUsdc} /> },
    { label: "Is max", value: d.isMax ? "yes" : "no" },
    { label: "Settled USDC", value: <Money micro={d.settledUsdcAmountMicroUsdc} /> },
    { label: "Settled shares", value: <Money micro={d.settledShareAmountMicroUsdc} /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
  ],
};

export const vaultApprovalConfig: EntityConfig<VaultApprovalDoc> = {
  key: "vaultApproval",
  collection: COLLECTIONS.vaultApprovals,
  title: "Vault Approval",
  pluralTitle: "Vault Approvals",
  ownerField: "owner",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    monoCol<P<VaultApprovalDoc>>("id", "ID", (r) => r.id),
    stateCol<P<VaultApprovalDoc>>((r) => r.state),
    textCol<P<VaultApprovalDoc>>("vault", "Vault", (r) => r.vault),
    relatedCol<P<VaultApprovalDoc>>("owner", "User", "user", (r) => r.owner),
    timeCol<P<VaultApprovalDoc>>("createdAt", "Created", (r) => r.createdAt),
  ],
  subtitle: (d) => <Mono value={d.id} />,
  detailFields: (d) => [
    { label: "Vault", value: <RelatedLink entity="vault" id={d.vault} label={d.vault} /> },
    { label: "Wallet", value: <RelatedLink entity="wallet" id={d.wallet} /> },
    { label: "User", value: <RelatedLink entity="user" id={d.owner} /> },
    { label: "Owner operation", value: <RelatedLink entity="ownerOperation" id={d.ownerOperation} /> },
    { label: "Approved", value: <Timestamp value={d.approvedAt} absolute /> },
    { label: "Revoked", value: <Timestamp value={d.revokedAt} absolute /> },
    { label: "Failure reason", value: d.failureReason ?? "—", full: true },
  ],
};

export const vaultConfig: EntityConfig<VaultDoc> = {
  key: "vault",
  collection: COLLECTIONS.vaults,
  title: "Vault",
  pluralTitle: "Vaults",
  defaultOrder: [["createdAt", "desc"]],
  columns: [
    textCol<P<VaultDoc>>("slug", "Slug", (r) => r.slug),
    textCol<P<VaultDoc>>("displayName", "Name", (r) => r.displayName),
    textCol<P<VaultDoc>>("protocol", "Protocol", (r) => r.protocol, { humanize: true }),
    textCol<P<VaultDoc>>("risk", "Risk", (r) => r.riskTier, { humanize: true }),
    {
      id: "enabled",
      header: "Enabled",
      accessorFn: (r: P<VaultDoc>) => (r.enabled ? "yes" : "no"),
      cell: (ctx) => (
        <Badge color={ctx.row.original.enabled ? "green" : "gray"} variant="light">
          {ctx.row.original.enabled ? "enabled" : "disabled"}
        </Badge>
      ),
    },
  ],
  subtitle: (d) => <Mono value={d.slug} copy />,
  detailFields: (d) => [
    { label: "Display name", value: d.displayName },
    { label: "Protocol", value: humanize(d.protocol) },
    { label: "Risk tier", value: humanize(d.riskTier) },
    { label: "Spendable", value: <Bps bps={d.spendablePercentBps} /> },
    { label: "Enabled", value: d.enabled ? "yes" : "no" },
    { label: "Underlying asset", value: <Mono value={d.underlyingAsset} /> },
    { label: "Pool address", value: <ExternalMono value={d.poolAddress} href={addressUrl(d.poolAddress)} /> },
    {
      label: "Share token",
      value: <ExternalMono value={d.shareTokenAddress} href={addressUrl(d.shareTokenAddress)} />,
    },
  ],
  related: (d) => [
    {
      title: "Positions",
      entity: "earnPosition",
      collection: COLLECTIONS.earnPositions,
      fkField: "vault",
      value: d.slug,
      orderBy: [["updatedAt", "desc"]],
    },
    {
      title: "Operations",
      entity: "earnOperation",
      collection: COLLECTIONS.earnOperations,
      fkField: "vault",
      value: d.slug,
      orderBy: [["createdAt", "desc"]],
    },
  ],
};
